import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, FileDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useApp } from "@/context/AppContext";
import { downloadPdf, errMsg, fmtCompact, useApi } from "@/lib/api";

export function ReportList({ rows, activeId, base, testid }) {
  const [q, setQ] = useState("");
  const shown = rows.filter((r) => `${r.name} ${r.code || ""}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="panel lg:sticky lg:top-24 flex flex-col max-h-[40vh] lg:max-h-[calc(100vh-8rem)]" data-testid={testid}>
      <div className="p-3 border-b border-slate-200 relative">
        <Search className="h-4 w-4 absolute left-6 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search" className="pl-9 rounded-sm" data-testid={`${testid}-search`} />
      </div>
      <ul className="overflow-y-auto flex-1">
        {shown.map((r) => (
          <li key={r.id}>
            <Link to={`${base}/${r.id}`} data-testid={`${testid}-item-${r.id}`}
              className={`flex items-center justify-between gap-3 px-4 py-2.5 text-sm border-l-2 transition-colors duration-200 ${r.id === activeId ? "border-amber-600 bg-amber-50/60 text-slate-900" : "border-transparent text-slate-600 hover:bg-slate-50"}`}>
              <span className="truncate">{r.name}</span>
              <span className="num text-[11px] text-slate-400 shrink-0">{fmtCompact(r.net_assets)}</span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function useReportPage(kindFilter, base, id) {
  const api = useApi();
  const navigate = useNavigate();
  const { period } = useApp();
  const [list, setList] = useState([]);
  const [rep, setRep] = useState(null);

  useEffect(() => {
    if (period) api.get("/entities", { params: { period } }).then((r) => setList(r.data.entities));
  }, [api, period]);

  const rows = useMemo(() => list.filter(kindFilter), [list, kindFilter]);

  useEffect(() => {
    if (!id && rows.length) navigate(`${base}/${rows[0].id}`, { replace: true });
  }, [id, rows, base, navigate]);

  useEffect(() => {
    if (!id || !period) return;
    setRep(null);
    const path = base.includes("networth") ? "networth" : "entity";
    api.get(`/reports/${path}/${id}`, { params: { period } }).then((r) => setRep(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [api, id, period, base]);

  return { api, period, rows, rep };
}

export const PdfButton = ({ api, url, name, label = "Download PDF", testid = "pdf-download-button", variant = "default" }) => {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      await downloadPdf(api, url, name);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button variant={variant} onClick={go} disabled={busy} className="rounded-sm" data-testid={testid}>
      <FileDown className="h-4 w-4 mr-2" />{busy ? "Preparing…" : label}
    </Button>
  );
};
